/**
 * Role Helper Utilities
 * Handles the unified role system (Employer + Worker)
 * Maps database roles (employer, owner, job_seeker, contractor) to UI roles
 */

/**
 * Get unified role from database role
 * @param {string} dbRole - Role from database (employer, owner, job_seeker, contractor)
 * @returns {string} - Unified role ('employer' or 'worker')
 */
export const getUnifiedRole = (dbRole) => {
  if (!dbRole) return null;
  
  if (dbRole === 'employer' || dbRole === 'owner') {
    return 'employer';
  }
  
  if (dbRole === 'job_seeker' || dbRole === 'contractor') {
    return 'worker';
  }
  
  // Admin role stays as is
  if (dbRole === 'admin') {
    return 'admin';
  }
  
  return null;
};

/**
 * Check if user can post jobs/gigs
 * @param {string} dbRole - Role from database
 * @returns {boolean}
 */
export const canPostJobs = (dbRole) => {
  return dbRole === 'employer' || dbRole === 'owner';
};

/**
 * Check if user can apply to jobs/gigs
 * @param {string} dbRole - Role from database
 * @returns {boolean}
 */
export const canApplyToJobs = (dbRole) => {
  return dbRole === 'job_seeker' || dbRole === 'contractor';
};

/**
 * Get display name for role
 * @param {string} dbRole - Role from database
 * @returns {string}
 */
export const getRoleDisplayName = (dbRole) => {
  const unifiedRole = getUnifiedRole(dbRole);
  
  if (unifiedRole === 'employer') return 'Employer';
  if (unifiedRole === 'worker') return 'Worker';
  if (unifiedRole === 'admin') return 'Admin';
  
  return 'User';
};

/**
 * Get dashboard route for role
 * @param {string} dbRole - Role from database
 * @returns {string}
 */
export const getDashboardRoute = (dbRole) => {
  if (canPostJobs(dbRole)) return '/employer-dashboard';
  if (canApplyToJobs(dbRole)) return '/job-seeker-dashboard';
  if (dbRole === 'admin') return '/admin';
  
  return '/';
};

/**
 * Check if user is employer (can post)
 * @param {object} profile - User profile object
 * @returns {boolean}
 */
export const isEmployer = (profile) => {
  return profile && canPostJobs(profile.role);
};

/**
 * Check if user is worker (can apply)
 * @param {object} profile - User profile object
 * @returns {boolean}
 */
export const isWorker = (profile) => {
  return profile && canApplyToJobs(profile.role);
};

/**
 * Check if user is admin
 * @param {object} profile - User profile object
 * @returns {boolean}
 */
export const isAdmin = (profile) => {
  return profile && profile.role === 'admin';
};
